import { useEffect, useState, useCallback } from "react";
import api from "../api/api";
import ConfirmModal from "../components/ConfirmModal";
import CommonModal from "../components/CommonModal";
import SearchBar from "../components/SearchBar"; // Import your new component
import "../styles/UnitsPage.css";
import "../styles/CommonModal.css";
import InputFeild from "../components/InputField";
import Button from "../components/Button";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
 
const UnitsPage = () => {
  const [units, setUnits] = useState([]); // Original Source
  const [displayUnits, setDisplayUnits] = useState([]); // Filtered Source
  const [unitName, setUnitName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
 
  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
 
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
 
  // Edit & delete states
  const [isEditMode, setIsEditMode] = useState(false);
  const [editUnitId, setEditUnitId] = useState(null);
  const [selectedUnitId, setSelectedUnitId] = useState(null);
 
  const getToken = () => localStorage.getItem("token");
 
  /* ================= FETCH UNITS ================= */
  const fetchUnits = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/units", {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = Array.isArray(res.data?.units) ? res.data.units : [];
      setUnits(data);
      setDisplayUnits(data); // Initial set
    } catch {
      setError("Failed to load units");
    } finally {
      setLoading(false);
    }
  }, []);
 
  useEffect(() => {
    fetchUnits();
  }, [fetchUnits]);
 
  /* ================= MODAL LOGIC ================= */
  const handleSaveUnit = () => {
    if (!unitName.trim()) {
      setError("Unit name is required");
      return;
    }
 
    const payload = {
      unit_name: unitName.charAt(0).toUpperCase() + unitName.slice(1),
    };
 
    const request = isEditMode
      ? api.put(`/units/${editUnitId}`, payload, {
          headers: { Authorization: `Bearer ${getToken()}` },
        })
      : api.post("/units", payload, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
 
    request
      .then(() => {
        setSuccessMessage(
          isEditMode ? "Unit updated successfully" : "Unit added successfully",
        );
        setShowSuccessModal(true);
        resetModal();
        fetchUnits();
      })
      .catch(() => setError("Failed to save unit"));
  };
 
  const resetModal = () => {
    setShowAddModal(false);
    setUnitName("");
    setIsEditMode(false);
    setEditUnitId(null);
    setError("");
  };
 
  const handleEditClick = (unit) => {
    setIsEditMode(true);
    setEditUnitId(unit.id);
    setUnitName(unit.unit_name);
    setShowAddModal(true);
  };
 
  const handleDeleteClick = (id) => {
    setSelectedUnitId(id);
    setShowConfirm(true);
  };
 
  const confirmDelete = () => {
    api
      .delete(`/units/${selectedUnitId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      .then(() => {
        setShowConfirm(false);
        setSuccessMessage("Unit deleted successfully");
        setShowSuccessModal(true);
        fetchUnits();
      })
      .catch(() => setError("Failed to delete unit"));
  };
 
  const capitalizeFirstLetter = (value) => {
    if (!value) return "";
    return value.charAt(0).toUpperCase() + value.slice(1);
  };
 
  return (
    <div className="units-container overflow-y-auto overflow-x-hidden">
      {error && <div className="alert alert-danger">{error}</div>}
 
      <div
        className="position-sticky top-0 p-2"
        style={{ background: "#f1f5f9", zIndex: 10 }}
      >
        <SearchBar
          data={units}
          searchKey="unit_name"
          onFilteredData={setDisplayUnits}
          onAddClick={() => {
            resetModal();
            setShowAddModal(true);
          }}
          placeholder="Search Unit..."
          buttonLabel="+ Add Unit"
        />
      </div>
 
      <div className="table-header">
        <span>Unit Name</span>
        <span>Action</span>
      </div>
 
      <div className="unit-list">
        {loading ? (
          <p className="text-center">Loading...</p>
        ) : displayUnits.length === 0 ? (
          <p className="text-center">No units found</p>
        ) : (
          displayUnits.map((unit) => (
            <div className="unit-row" key={unit.id}>
              <span>{unit.unit_name}</span>
              <div className="action-icons">
                <span
                  className="edit-icon"
                  onClick={() => handleEditClick(unit)}
                >
                  <EditIcon />
                </span>
                <span
                  className="delete-icon"
                  onClick={() => handleDeleteClick(unit.id)}
                >
                  <DeleteIcon />
                </span>
              </div>
            </div>
          ))
        )}
      </div>
 
      {/* MODALS REMAIN THE SAME... */}
      <CommonModal
        show={showAddModal}
        title={isEditMode ? "Edit Unit" : "Add New Unit"}
        onClose={resetModal}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={resetModal}
              text="Cancel"
              btncls="bg-secondary m-0"
            />
            <Button
              variant="primary"
              onClick={handleSaveUnit}
              text={isEditMode ? "Update" : "Save"}
              btncls="m-0"
            />
          </>
        }
      >
        <InputFeild
          placeholder="Enter unit name"
          value={unitName}
          onChange={(e) => setUnitName(capitalizeFirstLetter(e.target.value))}
          inpcls="w-100"
        />
      </CommonModal>
 
      <CommonModal
        show={showSuccessModal}
        title="Success"
        onClose={() => setShowSuccessModal(false)}
        footer={
          <Button
            variant="primary"
            text="OK"
            onClick={() => setShowSuccessModal(false)}
            btncls="m-0"
          />
        }
      >
        <p>{successMessage}</p>
      </CommonModal>
 
      <ConfirmModal
        show={showConfirm}
        title="Delete Unit"
        message="Are you sure you want to delete this unit?"
        confirmText="Yes, Delete"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setShowConfirm(false)}
      />
    </div>
  );
};
 
export default UnitsPage;